<app-dialog-base [showLoadingSpinner]="uiState == uiStateEnum.ShowLoading">
  <span dialog-title>Edit Tax Rates</span>

  <ng-container dialog-content>
    @if (!isConfirming()) {
      <p class="form-description">Enter tax rates as percentages (e.g., 5 for 5%)</p>

      <form [formGroup]="taxRateForm">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>GST Rate (%)</mat-label>
          <input matInput type="number" data-testid="gst-rate-input" formControlName="gstRate" min="0" max="100" step="0.001">
          <mat-hint>Federal Goods and Services Tax</mat-hint>
          @if (taxRateForm.controls.gstRate.hasError('min') || taxRateForm.controls.gstRate.hasError('max')) {
            <mat-error data-testid="gst-rate-error">Rate must be between 0 and 100</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width margin-top-small" data-testid="pst-rate-field">
          <mat-label data-testid="pst-rate-label">{{ pstLabel() }} Rate (%)</mat-label>
          <input matInput type="number" data-testid="pst-rate-input" formControlName="pstRate" min="0" max="100" step="0.001">
          <mat-hint data-testid="pst-rate-hint">{{ pstHint() }}</mat-hint>
          @if (taxRateForm.controls.pstRate.hasError('min') || taxRateForm.controls.pstRate.hasError('max')) {
            <mat-error data-testid="pst-rate-error">Rate must be between 0 and 100</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width margin-top-small">
          <mat-label>HST Rate (%)</mat-label>
          <input matInput type="number" data-testid="hst-rate-input" formControlName="hstRate" min="0" max="100" step="0.001">
          <mat-hint>Harmonized Sales Tax</mat-hint>
          @if (taxRateForm.controls.hstRate.hasError('min') || taxRateForm.controls.hstRate.hasError('max')) {
            <mat-error data-testid="hst-rate-error">Rate must be between 0 and 100</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width margin-top-small" data-testid="effective-date-field">
          <mat-label>Effective Date</mat-label>
          <input matInput [matDatepicker]="effectiveDatePicker" formControlName="effectiveDate" [min]="minEffectiveDate" data-testid="effective-date-input">
          <mat-hint>The date this rate change takes effect</mat-hint>
          <mat-datepicker-toggle matSuffix [for]="effectiveDatePicker"></mat-datepicker-toggle>
          <mat-datepicker #effectiveDatePicker></mat-datepicker>
          @if (taxRateForm.controls.effectiveDate.hasError('required')) {
            <mat-error data-testid="effective-date-error">Effective date is required</mat-error>
          }
          @if (taxRateForm.controls.effectiveDate.hasError('matDatepickerMin')) {
            <mat-error>Effective date cannot be in the past</mat-error>
          }
        </mat-form-field>
      </form>

      @if (isUnchanged) {
        <div class="alert alert-info d-flex align-items-center gap-2 mt-3" data-testid="rates-unchanged-warning">
          <mat-icon class="flex-shrink-0">info</mat-icon>
          <span>The rates are unchanged. Update at least one rate before saving.</span>
        </div>
      } @else if (isEffectiveDateToday) {
        <div class="alert alert-warning d-flex align-items-center gap-2 mt-3" data-testid="immediate-effect-warning">
          <mat-icon class="flex-shrink-0">warning</mat-icon>
          <span>This change will take effect immediately and apply to all new purchases today.</span>
        </div>
      }
    } @else {
      <p class="form-description" data-testid="confirmation-description">
        @if (isEffectiveDateToday) {
          Please confirm you want to apply these tax rates <strong>immediately</strong>. This will affect all new purchases today.
        } @else {
          Please confirm you want to schedule these tax rate changes for <strong>{{ taxRateForm.controls.effectiveDate.value?.toFormat('MMMM d, yyyy') }}</strong>.
        }
      </p>

      <table class="confirmation-table" data-testid="confirmation-table">
        <thead>
          <tr>
            <th>Tax</th>
            <th>Current Rate</th>
            <th>New Rate</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>GST</td>
            <td data-testid="confirm-current-gst">{{ formatRate(dialogData.currentRate.GstRate) }}</td>
            <td data-testid="confirm-new-gst">{{ taxRateForm.controls.gstRate.value }}%</td>
          </tr>
          <tr>
            <td>{{ pstLabel() }}</td>
            <td data-testid="confirm-current-pst">{{ formatRate(dialogData.currentRate.PstRate) }}</td>
            <td data-testid="confirm-new-pst">{{ taxRateForm.controls.pstRate.value }}%</td>
          </tr>
          <tr>
            <td>HST</td>
            <td data-testid="confirm-current-hst">{{ formatRate(dialogData.currentRate.HstRate) }}</td>
            <td data-testid="confirm-new-hst">{{ taxRateForm.controls.hstRate.value }}%</td>
          </tr>
        </tbody>
      </table>
    }
  </ng-container>

  <ng-container actions>
    @if (!isConfirming()) {
      <button
        mat-raised-button
        color="primary"
        data-testid="save-tax-rates-button"
        (click)="save()"
        [disabled]="uiState === uiStateEnum.ShowLoading || isUnchanged">
        {{ saveButtonLabel }}
      </button>
    } @else {
      <button
        mat-button
        data-testid="back-to-edit-button"
        (click)="backToEdit()"
        [disabled]="uiState === uiStateEnum.ShowLoading">
        Back
      </button>
      <button
        mat-raised-button
        color="primary"
        data-testid="confirm-tax-rates-button"
        (click)="confirm()"
        [disabled]="uiState === uiStateEnum.ShowLoading">
        <mat-icon>check</mat-icon>
        Confirm
      </button>
    }
  </ng-container>
</app-dialog-base>
